import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/apiClient";
import { Monogram } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentReturn() {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isSuccess = location.pathname.endsWith("/success");
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(isSuccess ? "checking" : "cancelled");
  const attempts = useRef(0);

  useEffect(() => {
    if (!isSuccess || !sessionId) return;
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch (e) {}
      if (attempts.current >= 10) {
        setStatus("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [isSuccess, sessionId]);

  const config = {
    checking: { icon: <Loader2 className="h-12 w-12 animate-spin" />, title: "Confirming payment", sub: "Please wait a moment…" },
    paid: { icon: <CheckCircle2 className="h-12 w-12" />, title: "Payment successful", sub: "Your invoice has been marked as paid. Thank you." },
    cancelled: { icon: <XCircle className="h-12 w-12" />, title: "Payment cancelled", sub: "No charge was made. You can try again anytime." },
    failed: { icon: <XCircle className="h-12 w-12" />, title: "Payment failed", sub: "Something went wrong. Please try again." },
    timeout: { icon: <Loader2 className="h-12 w-12" />, title: "Still processing", sub: "This is taking longer than usual. Check your invoices shortly." },
  }[status];

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="text-center max-w-sm">
        <div className="flex justify-center mb-6 text-foreground">{config.icon}</div>
        <h1 className="font-brand font-light text-3xl tracking-tight mb-2" data-testid="payment-return-title">
          {config.title}
        </h1>
        <p className="text-muted-foreground text-sm mb-8">{config.sub}</p>
        <Button onClick={() => navigate("/")} className="rounded-full" data-testid="payment-return-home">
          Back to dashboard
        </Button>
      </div>
    </div>
  );
}
